import { MB_EventEmmiter, MB_EVENT_EMMITER_EVENT_TYPE } from '@/utils/general/event-emmiter';
import { Platform } from 'react-native';
import {
  ErrorCode,
  EventSubscription,
  ProductSubscription,
  ProductSubscriptionAndroid,
  ProductSubscriptionAndroidOfferDetails,
  Purchase,
  initConnection,
  endConnection,
  fetchProducts,
  requestPurchase,
  purchaseUpdatedListener,
  finishTransaction,
  purchaseErrorListener,
  getAvailablePurchases,
  getReceiptDataIOS,
} from 'react-native-iap';
import { envs } from '../../env';
import { paymentAndOrdersApiCalls } from '../apiCalls/paymentAndOrdersApiCalls';
import { ANDROID_BASE_PLAN_IDS, PAYMENT_PROVIDER } from '../constants/constants';
import { AcknowledgeRequest, PAYMENT_STATUS } from '../typesAndInterfaces/typesAndInterfaces';
import { mbShowToast } from '@/components/general/toast';

export enum MB_MOBILE_PURCHASE_EVENT_TYPE {
  'MOBILE_PURCHASE_UPDATE_SUCCESS' = 'MOBILE_PURCHASE_UPDATE_SUCCESS',
  'MOBILE_PURCHASE_UPDATE_PENDING' = 'MOBILE_PURCHASE_UPDATE_PENDING',
  'MOBILE_PURCHASE_UPDATE_ERROR' = 'MOBILE_PURCHASE_UPDATE_ERROR',
  'MOBILE_PURCHASE_NEEDS_UPDATE' = 'MOBILE_PURCHASE_NEEDS_UPDATE',
  'MOBILE_PURCHASE_CANCELLED' = 'MOBILE_PURCHASE_CANCELLED',
}

const emitEvent = (type: MB_MOBILE_PURCHASE_EVENT_TYPE, error?: any) => {
  MB_EventEmmiter.emit(MB_EVENT_EMMITER_EVENT_TYPE.message, { origin: envs.WEBSITE_BASE_URL, data: { type, error } });
};

async function sendAcknowledgeRequest(requestParams: AcknowledgeRequest<PAYMENT_PROVIDER>, purchase: Purchase) {
  paymentAndOrdersApiCalls
    .acknowledgePurchase(requestParams)
    .then(async newPaymentStatus => {
      console.log('Success when sending acknowledge payment request', newPaymentStatus);

      if (newPaymentStatus === PAYMENT_STATUS.pending) {
        emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_UPDATE_PENDING);
      } else {
        emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_UPDATE_SUCCESS);
        finishTransaction({ purchase, isConsumable: false }).catch(error => {
          console.error('Error when finishing transaction after acknowledge', error);
        });
        // TODO: Why do we need to call finishTransaction? Maybe backend should do this?
      }
    })
    .catch(error => {
      console.error('Error when sending acknowledge payment request', error);
      emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_UPDATE_ERROR, error);
    });
}

// The backend acknowledge endpoint validates Apple purchases with the App Store
// verifyReceipt flow, which needs the base64 app receipt — NOT the per-purchase
// StoreKit 2 JWS that v14 puts in purchase.purchaseToken. getReceiptDataIOS()
// returns that same app receipt v13 exposed as purchase.transactionReceipt.
async function getAppleReceiptData(): Promise<string | undefined> {
  try {
    return (await getReceiptDataIOS()) ?? undefined;
  } catch (error) {
    console.error('Error: could not read the App Store receipt', error);
    return undefined;
  }
}

// Google Play `recurrenceMode` of a pricing phase: 1 = INFINITE_RECURRING (auto-renewing),
// 2 = FINITE_RECURRING, 3 = NON_RECURRING (prepaid base plan).
const PLAY_RECURRENCE_NON_RECURRING = 3;

function isAutoRenewingOffer(offer: ProductSubscriptionAndroidOfferDetails): boolean {
  const phases = offer.pricingPhases?.pricingPhaseList ?? [];
  const basePhase = phases[phases.length - 1];
  return basePhase !== undefined && basePhase.recurrenceMode !== PLAY_RECURRENCE_NON_RECURRING;
}

/**
 * Picks the Google Play offer to purchase (and to price) for a subscription product.
 *
 * Play returns one entry per purchasable base plan / offer, in no guaranteed order, so
 * "first offer" could be a prepaid or legacy plan. Preference:
 *  1. the plain base plan (no offerId) whose basePlanId is configured in
 *     ANDROID_BASE_PLAN_IDS for this SKU;
 *  2. otherwise the first plain base plan that is auto-renewing, with a warning;
 *  3. otherwise the first plain base plan at all (prepaid), with a warning;
 *  4. otherwise the first offer of any kind.
 * A prepaid plan is therefore never chosen while an auto-renewing one exists.
 */
export function selectAndroidSubscriptionOffer(subscription: ProductSubscriptionAndroid): ProductSubscriptionAndroidOfferDetails | undefined {
  const offers = subscription.subscriptionOfferDetailsAndroid ?? [];
  if (offers.length === 0) {
    return undefined;
  }

  const basePlans = offers.filter(offer => !offer.offerId);
  const expectedBasePlanId = ANDROID_BASE_PLAN_IDS[subscription.id];
  const configured = expectedBasePlanId ? basePlans.find(offer => offer.basePlanId === expectedBasePlanId) : undefined;
  if (configured) {
    return configured;
  }

  const fallback = basePlans.find(isAutoRenewingOffer) ?? basePlans[0] ?? offers[0];
  console.warn(
    `Google Play returned no base plan "${expectedBasePlanId ?? '(unconfigured)'}" for ${subscription.id}; ` +
      `falling back to "${fallback.basePlanId}"${fallback.offerId ? ` offer "${fallback.offerId}"` : ''} ` +
      `(${isAutoRenewingOffer(fallback) ? 'auto-renewing' : 'prepaid'}). Available: ${offers.map(offer => offer.basePlanId).join(', ')}`
  );
  return fallback;
}

// Note: You can read more about how to use react-native-iap at https://hyochan.github.io/react-native-iap
class MB_MobilePurchase {
  #purchaseUpdateSubscription: EventSubscription | undefined;
  #purchaseErrorSubscription: EventSubscription | undefined;
  #isConnected: boolean = false;

  constructor() {}

  getIsConnected() {
    return this.#isConnected;
  }

  eventIsFromMobilePurchase(event: any): boolean {
    return Object.keys(MB_MOBILE_PURCHASE_EVENT_TYPE).indexOf(event) !== -1;
  }

  async connect(): Promise<boolean> {
    // Cleanup, close listeners and connections just in case if they were somehow active.
    this.disconnect();

    try {
      this.#isConnected = await initConnection();
      console.log('Mobile payment connection established status: ' + this.#isConnected);

      // v13 flushed Android's "ghost" failed-pending purchase cache here. Billing
      // Library 8 (rn-iap v14) removed that legacy cache API: real pending purchases
      // now re-fire through purchaseUpdatedListener below on every connect, where we
      // acknowledge them — consuming them here would destroy valid entitlements.

      this.#purchaseUpdateSubscription = purchaseUpdatedListener(async purchase => {
        const { purchaseState, purchaseToken, transactionId, productId } = purchase;
        console.log('Received purchase update for', productId);

        if (!productId) {
          console.error('Error: Invalid purchase state. No product ID', purchase);
          return;
        }

        if (Platform.OS === 'ios') {
          const receiptData = await getAppleReceiptData();
          if (!receiptData) {
            console.error('Error: Invalid purchase state. No receipt data', purchase);
            return;
          }

          const requestParams: AcknowledgeRequest<PAYMENT_PROVIDER.apple> = {
            paymentProvider: PAYMENT_PROVIDER.apple,
            productId,
            transactionId: transactionId ?? undefined,
            receiptData,
          };

          sendAcknowledgeRequest(requestParams, purchase);
        } else if (Platform.OS === 'android') {
          if (!purchaseToken) {
            console.error('Error: Invalid purchase state. No purchase token', purchase);
            return;
          }

          if (purchaseState === 'pending' || purchaseState === 'purchased') {
            const requestParams: AcknowledgeRequest<PAYMENT_PROVIDER.google> = {
              paymentProvider: PAYMENT_PROVIDER.google,
              productId,
              purchaseToken,
            };

            sendAcknowledgeRequest(requestParams, purchase);
          }
        }
      });

      this.#purchaseErrorSubscription = purchaseErrorListener(error => {
        if (error.code === ErrorCode.UserCancelled) {
          emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_CANCELLED);
        } else {
          console.error('Mobile payment purchase error', error);
          emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_UPDATE_ERROR, error);
        }
      });
    } catch (error) {
      console.error('Error when initializing mobile purchase', error);
    }

    return this.#isConnected;
  }

  async disconnect(withoutError?: boolean) {
    try {
      if (this.#purchaseUpdateSubscription) {
        this.#purchaseUpdateSubscription.remove();
        this.#purchaseUpdateSubscription = undefined;
      }

      if (this.#purchaseErrorSubscription) {
        this.#purchaseErrorSubscription.remove();
        this.#purchaseErrorSubscription = undefined;
      }

      await endConnection();
      this.#isConnected = false;
    } catch (error) {
      if (!withoutError) {
        console.error('SaikLog: Error when ending mobile purchase connection', error);
      }
    }
  }

  /**
   * Starts an auto-renewing subscription purchase (premium_annual). On Android a
   * purchase requires the `offerToken` of the SKU's base-plan offer, which we look
   * up via fetchProducts first. iOS only needs the sku.
   * Store-level errors (including user cancellation) surface via purchaseErrorListener.
   */
  async requestSubscription({ sku }: { sku: string }) {
    try {
      if (!this.#isConnected) {
        await this.connect();
      }

      if (Platform.OS === 'android') {
        const subscriptions = await this.getSubscriptions([sku]);
        const androidSub = subscriptions.find(sub => sub.id === sku) as ProductSubscriptionAndroid | undefined;
        const offerToken = androidSub ? selectAndroidSubscriptionOffer(androidSub)?.offerToken : undefined;

        if (!offerToken) {
          console.error(`Error when requesting a subscription: no base-plan offer/offerToken for ${sku}`);
          emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_UPDATE_ERROR);
          return;
        }

        return await requestPurchase({
          type: 'subs',
          request: {
            google: {
              skus: [sku],
              subscriptionOffers: [{ sku, offerToken }],
            },
          },
        });
      }

      // iOS. rn-iap 14.5 declares both `ios` and `apple` on the request type but its
      // runtime only reads `request.ios` (throws "The `sku` property is required" otherwise).
      return await requestPurchase({
        type: 'subs',
        request: {
          ios: {
            sku,
            andDangerouslyFinishTransactionAutomatically: false,
          },
        },
      });
    } catch (error) {
      console.error('Error when requesting a subscription', { error });
    }
  }

  /**
   * Fetches subscription products (localized price, base-plan offers) from the store.
   * Returns [] if the store connection / lookup fails so the UI can fall back gracefully.
   */
  async getSubscriptions(productIds: string[]): Promise<ProductSubscription[]> {
    try {
      if (!this.#isConnected) {
        await this.connect();
      }

      const products = await fetchProducts({ skus: productIds, type: 'subs' });
      return (products ?? []) as ProductSubscription[];
    } catch (error) {
      console.error('Error when fetching subscriptions', { error });
      return [];
    }
  }

  /**
   * Re-validates any purchases already tied to the store account (App Store / Google Play)
   * and re-acknowledges them with the backend so premium is restored after a reinstall or
   * device change. Recognizes BOTH the active annual subscription (premium_annual) AND a
   * grandfathered user's legacy one-time 'premium' — `onlyIncludeActiveItemsIOS: false`
   * ensures the owned-but-not-active-subscription one-time product is still returned. The
   * backend acknowledge validates whichever productId comes back. Returns the number of
   * purchases found.
   */
  async restorePurchases(): Promise<number> {
    if (!this.#isConnected) {
      await this.connect();
    }

    const availablePurchases = await getAvailablePurchases({ onlyIncludeActiveItemsIOS: false });

    // The app-level receipt covers every transaction, so one read serves all purchases.
    const receiptData = Platform.OS === 'ios' ? await getAppleReceiptData() : undefined;

    for (const purchase of availablePurchases) {
      const { purchaseToken, transactionId, productId } = purchase;

      if (!productId) {
        continue;
      }

      if (Platform.OS === 'ios') {
        if (!receiptData) {
          continue;
        }

        const requestParams: AcknowledgeRequest<PAYMENT_PROVIDER.apple> = {
          paymentProvider: PAYMENT_PROVIDER.apple,
          productId,
          transactionId: transactionId ?? undefined,
          receiptData,
        };

        sendAcknowledgeRequest(requestParams, purchase);
      } else if (Platform.OS === 'android') {
        if (!purchaseToken) {
          continue;
        }

        const requestParams: AcknowledgeRequest<PAYMENT_PROVIDER.google> = {
          paymentProvider: PAYMENT_PROVIDER.google,
          productId,
          purchaseToken,
        };

        sendAcknowledgeRequest(requestParams, purchase);
      }
    }

    return availablePurchases.length;
  }

  /** Asks the payment context to re-read the entitlement from the backend. */
  requestPaymentStatusRefresh() {
    emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_NEEDS_UPDATE);
  }

  // This allows us to consume store-side purchases and basically reset purchase
  // state for testing. Only the store transactions are consumed; the backend's
  // debugDeleteOrder endpoint was removed, so server-side orders stay untouched.
  async consumeProductsForDebug(): Promise<boolean> {
    try {
      let purchaseResultText = 'Test';
      const availablePurchases = await getAvailablePurchases();

      if (availablePurchases.length === 0) {
        purchaseResultText = 'No purchases to clear';
      }

      await Promise.all(
        availablePurchases.map(async purchase => {
          // v14's finishTransaction resolves void on success and throws on failure
          // (the v13 boolean/response-object result is gone).
          await finishTransaction({ purchase, isConsumable: true });
          purchaseResultText = `Finished ${purchase.productId}`;
        })
      );

      emitEvent(MB_MOBILE_PURCHASE_EVENT_TYPE.MOBILE_PURCHASE_NEEDS_UPDATE);
      mbShowToast({ text1: 'Purchase cleared', text2: purchaseResultText, type: 'success' });
      return true;
    } catch {
      mbShowToast({ text1: 'Purchase clear failed!', type: 'error' });
      return false;
    }
  }
}

const instance = new MB_MobilePurchase();
export default instance;
